import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, HeartHandshake, Users, Building2, Info } from "lucide-react";
import PageHero from "../components/PageHero";
import SectionHeader from "../components/SectionHeader";
import { useLanguage } from "../contexts/LanguageContext";
import { IMAGES, CONTACT, LOGO_URL_LIGHT, DONATION_PACKAGES } from "../data/site";
import { postVolunteer, postPartner, createDonationCheckout } from "../lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const inputCls =
  "w-full rounded-xl border border-neutral-200 bg-neutral-50 px-4 py-3 text-sm font-body placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-[#0B5D1E]/30 focus:border-[#0B5D1E] transition";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-neutral-700 font-heading">
        {label} {required && <span className="text-[#B5651D]">*</span>}
      </span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function VolunteerForm() {
  const { t } = useLanguage();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    skills: "",
    availability: "",
    message: "",
  });
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await postVolunteer(form);
      toast.success(res.message);
      setForm({ full_name: "", email: "", phone: "", skills: "", availability: "", message: "" });
    } catch (err) {
      toast.error("Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4" data-testid="volunteer-form">
      <Field label={t.getInvolved.fields.fullName} required>
        <input type="text" required value={form.full_name} onChange={update("full_name")} className={inputCls} data-testid="volunteer-name" />
      </Field>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label={t.getInvolved.fields.email} required>
          <input type="email" required value={form.email} onChange={update("email")} className={inputCls} data-testid="volunteer-email" />
        </Field>
        <Field label={t.getInvolved.fields.phone}>
          <input type="tel" value={form.phone} onChange={update("phone")} className={inputCls} data-testid="volunteer-phone" />
        </Field>
      </div>
      <Field label={t.getInvolved.fields.skills}>
        <input type="text" value={form.skills} onChange={update("skills")} className={inputCls} data-testid="volunteer-skills" />
      </Field>
      <Field label={t.getInvolved.fields.availability}>
        <input type="text" value={form.availability} onChange={update("availability")} className={inputCls} placeholder="e.g., Weekends" data-testid="volunteer-availability" />
      </Field>
      <Field label={t.getInvolved.fields.message}>
        <textarea rows={3} value={form.message} onChange={update("message")} className={`${inputCls} resize-none`} data-testid="volunteer-message" />
      </Field>
      <button type="submit" disabled={submitting} className="gk-btn-primary w-full justify-center disabled:opacity-60" data-testid="volunteer-submit">
        {submitting ? <><Loader2 size={18} className="animate-spin" /> {t.common.sending}</> : t.common.submit}
      </button>
    </form>
  );
}

function PartnerForm() {
  const { t } = useLanguage();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    organization: "",
    contact_name: "",
    email: "",
    phone: "",
    partnership_type: "",
    message: "",
  });
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await postPartner(form);
      toast.success(res.message);
      setForm({ organization: "", contact_name: "", email: "", phone: "", partnership_type: "", message: "" });
    } catch (err) {
      toast.error("Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4" data-testid="partner-form">
      <Field label={t.getInvolved.fields.organization} required>
        <input type="text" required value={form.organization} onChange={update("organization")} className={inputCls} data-testid="partner-org" />
      </Field>
      <Field label={t.getInvolved.fields.contactName} required>
        <input type="text" required value={form.contact_name} onChange={update("contact_name")} className={inputCls} data-testid="partner-contact" />
      </Field>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label={t.getInvolved.fields.email} required>
          <input type="email" required value={form.email} onChange={update("email")} className={inputCls} data-testid="partner-email" />
        </Field>
        <Field label={t.getInvolved.fields.phone}>
          <input type="tel" value={form.phone} onChange={update("phone")} className={inputCls} data-testid="partner-phone" />
        </Field>
      </div>
      <Field label={t.getInvolved.fields.partnershipType}>
        <input type="text" value={form.partnership_type} onChange={update("partnership_type")} className={inputCls} data-testid="partner-type" />
      </Field>
      <Field label={t.getInvolved.fields.message}>
        <textarea rows={3} value={form.message} onChange={update("message")} className={`${inputCls} resize-none`} data-testid="partner-message" />
      </Field>
      <button type="submit" disabled={submitting} className="gk-btn-secondary w-full justify-center disabled:opacity-60" data-testid="partner-submit">
        {submitting ? <><Loader2 size={18} className="animate-spin" /> {t.common.sending}</> : t.common.submit}
      </button>
    </form>
  );
}

function DonatePanel() {
  const { t } = useLanguage();
  const [selectedPkg, setSelectedPkg] = useState("friend");
  const [customAmount, setCustomAmount] = useState("");
  const [donorName, setDonorName] = useState("");
  const [donorEmail, setDonorEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const onDonate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const isCustom = selectedPkg === "custom";
      const amt = parseFloat(customAmount);
      if (isCustom && (!amt || amt < 1)) {
        toast.error("Please enter a valid amount ($1 minimum)");
        setLoading(false);
        return;
      }
      const payload = {
        origin_url: window.location.origin,
        donor_name: donorName || undefined,
        donor_email: donorEmail || undefined,
      };
      if (isCustom) payload.custom_amount = amt;
      else payload.package_id = selectedPkg;

      const res = await createDonationCheckout(payload);
      if (res.url) {
        window.location.href = res.url;
      } else {
        throw new Error("No checkout URL");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not start checkout. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Checkout failed");
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="donate-panel">
      <div className="rounded-3xl bg-white border border-black/5 shadow-xl p-7">
        <h4 className="font-heading font-bold text-xl text-[#0B5D1E]">
          {t.common.chooseAmount}
        </h4>

        <form onSubmit={onDonate} className="mt-5 space-y-4">
          <div className="grid grid-cols-3 gap-2" data-testid="donation-presets">
            {DONATION_PACKAGES.map((pkg) => (
              <button
                key={pkg.id}
                type="button"
                onClick={() => setSelectedPkg(pkg.id)}
                data-testid={`donate-pkg-${pkg.id}`}
                className={`rounded-2xl py-4 font-heading font-bold text-lg border-2 transition-all ${
                  selectedPkg === pkg.id
                    ? "bg-[#0B5D1E] text-white border-[#0B5D1E] shadow-lg"
                    : "bg-white text-neutral-800 border-neutral-200 hover:border-[#0B5D1E]/40"
                }`}
              >
                ${pkg.amount}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setSelectedPkg("custom")}
              data-testid="donate-pkg-custom"
              className={`rounded-2xl py-4 font-heading font-bold text-sm border-2 transition-all ${
                selectedPkg === "custom"
                  ? "bg-[#B5651D] text-white border-[#B5651D] shadow-lg"
                  : "bg-white text-neutral-800 border-neutral-200 hover:border-[#B5651D]/40"
              }`}
            >
              {t.common.customAmount}
            </button>
          </div>

          {selectedPkg === "custom" && (
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500 font-semibold">$</span>
              <input
                type="number"
                min="1"
                max="10000"
                step="1"
                value={customAmount}
                onChange={(e) => setCustomAmount(e.target.value)}
                placeholder="Amount"
                data-testid="donate-custom-input"
                className="w-full rounded-xl border border-neutral-200 bg-neutral-50 pl-8 pr-4 py-3 text-base font-body focus:outline-none focus:ring-2 focus:ring-[#B5651D]/30 focus:border-[#B5651D] transition"
              />
            </div>
          )}

          <input
            type="text"
            value={donorName}
            onChange={(e) => setDonorName(e.target.value)}
            placeholder={t.common.donorNameOptional}
            data-testid="donate-name-input"
            className="w-full rounded-xl border border-neutral-200 bg-neutral-50 px-4 py-3 text-sm font-body focus:outline-none focus:ring-2 focus:ring-[#0B5D1E]/30 focus:border-[#0B5D1E] transition"
          />
          <input
            type="email"
            value={donorEmail}
            onChange={(e) => setDonorEmail(e.target.value)}
            placeholder={t.common.donorEmailOptional}
            data-testid="donate-email-input"
            className="w-full rounded-xl border border-neutral-200 bg-neutral-50 px-4 py-3 text-sm font-body focus:outline-none focus:ring-2 focus:ring-[#0B5D1E]/30 focus:border-[#0B5D1E] transition"
          />

          <button
            type="submit"
            disabled={loading}
            data-testid="donate-submit-btn"
            className="gk-btn-primary w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? (
              <><Loader2 size={18} className="animate-spin" /> {t.common.donating}</>
            ) : (
              <><HeartHandshake size={18} /> {t.common.donateWithCard}</>
            )}
          </button>

          <p className="text-xs text-neutral-500 text-center">
            Secure payment by Stripe · We never see your card details.
          </p>
        </form>
      </div>

      <div className="rounded-3xl bg-white border border-black/5 shadow-sm p-6">
        <h5 className="font-heading font-bold text-base">{t.contact.title}</h5>
        <p className="mt-3 text-sm text-neutral-700">
          <a href={`mailto:${CONTACT.email}`} className="text-[#0B5D1E] hover:underline block">{CONTACT.email}</a>
          <a href={`tel:${CONTACT.phone1.replace(/[^+\d]/g, "")}`} className="text-[#0B5D1E] hover:underline block mt-1">{CONTACT.phone1}</a>
        </p>
      </div>
    </div>
  );
}

export default function GetInvolved() {
  const { t } = useLanguage();

  return (
    <div data-testid="get-involved-page">
      <PageHero
        eyebrow={t.getInvolved.eyebrow}
        title={t.getInvolved.title}
        subtitle={t.getInvolved.subtitle}
        image={IMAGES.environment}
      />

      <section className="py-20">
        <div className="gk-container">
          <SectionHeader
            eyebrow={t.getInvolved.eyebrow}
            title={t.getInvolved.title}
            align="center"
          />

          <Tabs defaultValue="volunteer" className="mt-10 max-w-4xl mx-auto" data-testid="get-involved-tabs">
            <TabsList className="grid grid-cols-3 bg-neutral-100 rounded-full p-1 h-auto">
              <TabsTrigger value="volunteer" data-testid="tab-volunteer" className="rounded-full py-3 data-[state=active]:bg-[#0B5D1E] data-[state=active]:text-white font-heading">
                <Users size={16} className="mr-2" />
                {t.getInvolved.tabs.volunteer}
              </TabsTrigger>
              <TabsTrigger value="donate" data-testid="tab-donate" className="rounded-full py-3 data-[state=active]:bg-[#B5651D] data-[state=active]:text-white font-heading">
                <HeartHandshake size={16} className="mr-2" />
                {t.getInvolved.tabs.donate}
              </TabsTrigger>
              <TabsTrigger value="partner" data-testid="tab-partner" className="rounded-full py-3 data-[state=active]:bg-[#0A0A0A] data-[state=active]:text-white font-heading">
                <Building2 size={16} className="mr-2" />
                {t.getInvolved.tabs.partner}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="volunteer" className="mt-8">
              <div className="grid md:grid-cols-2 gap-8 items-start">
                <div>
                  <h3 className="font-heading font-bold text-2xl">{t.getInvolved.volunteer.title}</h3>
                  <p className="mt-4 text-neutral-700 leading-relaxed">{t.getInvolved.volunteer.body}</p>
                  <img src={LOGO_URL_LIGHT} alt="GKNL logo" className="mt-6 rounded-2xl h-80 w-full object-contain bg-white border border-black/5 p-6" />
                </div>
                <div className="bg-white rounded-[2rem] border border-black/5 shadow-xl p-7">
                  <VolunteerForm />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="donate" className="mt-8">
              <div className="grid md:grid-cols-2 gap-8 items-start">
                <div>
                  <h3 className="font-heading font-bold text-2xl">{t.getInvolved.donate.title}</h3>
                  <p className="mt-4 text-neutral-700 leading-relaxed">{t.getInvolved.donate.body}</p>
                  <img src={IMAGES.orphan} alt="" className="mt-6 rounded-2xl h-80 w-full object-contain bg-[#0B5D1E]/5" />
                </div>
                <DonatePanel />
              </div>
            </TabsContent>

            <TabsContent value="partner" className="mt-8">
              <div className="grid md:grid-cols-2 gap-8 items-start">
                <div>
                  <h3 className="font-heading font-bold text-2xl">{t.getInvolved.partner.title}</h3>
                  <p className="mt-4 text-neutral-700 leading-relaxed">{t.getInvolved.partner.body}</p>
                  <img src={IMAGES.humanitarian} alt="" className="mt-6 rounded-2xl h-80 w-full object-contain bg-[#0B5D1E]/5" />
                </div>
                <div className="bg-white rounded-[2rem] border border-black/5 shadow-xl p-7">
                  <PartnerForm />
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
}
